import { call, put, select, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import { Api } from '../../api/api';
import { API_ENDPOINTS } from '../../api/endpoints';
import {
  createAppointment,
  createAppointmentSuccess,
  createAppointmentFailure,
  type CreateAppointmentPayload,
} from '../slices/bookingSlice';
import type { RootState } from '../index';

function* createAppointmentSaga(action: PayloadAction<CreateAppointmentPayload>) {
  try {
    // Get franchise_id, userId, and accountId from Redux state
    const { franchiseId, userId, accountId } = yield select((state: RootState) => state.stylist);

    if (!franchiseId || !userId) {
      throw new Error('Franchise ID or User ID not found');
    }

    const payload = action.payload;

    // Prepare request body with new API format
    const requestBody = {
      customer_name: payload.customer_name,
      customer_contact: payload.customer_contact,
      customer_email: payload.customer_email,
      gender: payload.gender,
      stylist_id: payload.stylist_id,  // Now an array
      service_id: payload.service_id,  // Now an array
      appointment_date: payload.appointment_date,
      start_time: payload.start_time,
      status: payload.status,
      notes: payload.notes || '',
      discount: payload.discount,
      price: payload.price,
    };

    // Call API using Api wrapper with proper baseURL
    const response: { 
      success: boolean;
      message: string;
      data: any;
      error: null;
    } = yield call(
      Api.post,
      API_ENDPOINTS.APPOINTMENT.CREATE,
      requestBody,
      {
        franchise_id: franchiseId,
        userId: userId,
        account_id: accountId,
      }
    );

    // Dispatch success action with appointment data
    yield put(
      createAppointmentSuccess({
        appointmentId: `${response.data.appointment_date}_${response.data.start_time}`,
      })
    );
  } catch (error: any) {
    console.error('Create appointment error:', error);
    yield put(
      createAppointmentFailure(
        error.response?.data?.message || error.message || 'Failed to create appointment'
      )
    );
  }
}

export default function* bookingSaga() {
  yield takeLatest(createAppointment.type, createAppointmentSaga);
}
